import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import conversationService, { type ContactThread } from '@/services/conversationService';
import {
  applyMessageEventToThread,
  applyTakeoverEventToThread,
  mergeThreadRefresh,
  prependOlderSessions,
  type AgentTypingEvent,
  type StaffTypingEvent,
  type WhatsAppMessageEvent,
  type WhatsAppTakeoverEvent,
} from '@/lib/liveConversation';
import {
  applyChannelHealthEventToThread,
  sameAgentNumber,
  type WhatsAppChannelHealthEvent,
} from '@/lib/channelHealth';
import {
  applyWamidStatuses,
  createWamidStatusBuffer,
  needsSiblingResync,
  type WhatsAppMessageStatusEvent,
} from '@/lib/deliveryStatus';
import { createMutationClock, fetchUnlessOvertaken } from '@/lib/mutationClock';
import { serverNow } from '@/lib/serverClock';
import { useDegradedPolling, useLiveEvent } from '@/hooks/useLiveEvents';
import type { ThreadUpdater } from './useContactActions';

/** Typing events are ephemeral (no replay): a lost "done" must not leave the dots forever. */
export const AGENT_TYPING_TIMEOUT_MS = 60_000;
export const STAFF_TYPING_TIMEOUT_MS = 30_000;
/** A status of one part of a multi-part reply: wait this long for its siblings, then ask the server once. */
export const STATUS_RESYNC_DELAY_MS = 1_500;
/** Upper bound of pages loaded to reach a session the reader jumped to. */
const MAX_JUMP_PAGES = 10;

export interface ContactThreadState {
  thread: ContactThread | null;
  loading: boolean;
  error: string | null;
  loadingOlder: boolean;
  /** The agent is computing a reply (`whatsapp.agent_typing`). */
  agentTyping: boolean;
  /** A colleague types in the web app (`whatsapp.staff_typing`), by whom. */
  staffTyping: string | null;
  update: ThreadUpdater;
  /** Silent refetch of the newest page (what the stream could not tell). */
  refresh: () => void;
  /** Resolves true when older sessions were added. */
  loadOlder: () => Promise<boolean>;
  /** Pages back until the session is loaded; false if it never shows up. */
  ensureSession: (conversationId: number) => Promise<boolean>;
}

function sameAccount(a: string | null | undefined, b: string | null | undefined): boolean {
  return !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * The continuous WhatsApp chat of one contact (livecenter §5) with live
 * updates. Everything is matched by `contact_key` — a new conversation row
 * behind the same contact still lands here (finding B2).
 */
export function useContactThread(contactKey: string, me: string | null): ContactThreadState {
  const [thread, setThread] = useState<ContactThread | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [agentTyping, setAgentTyping] = useState(false);
  const [staffTyping, setStaffTyping] = useState<string | null>(null);
  // Local changes (event, optimistic update, POST answer) tick the clock; a
  // background refetch started before the latest tick is stale (mutationClock.ts).
  const [mutations] = useState(createMutationClock);
  const loadSeqRef = useRef(0);
  const threadRef = useRef<ContactThread | null>(null);
  const olderPromiseRef = useRef<Promise<boolean> | null>(null);
  // Events that arrive while the first load is in flight: the answer may be
  // older than they are, so they are applied on top of it (reducers are idempotent).
  const loadedRef = useRef(false);
  const pendingRef = useRef<Array<(thread: ContactThread) => ContactThread>>([]);
  const agentTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const staffTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Delivery statuses by wamid. Every thread state built below runs through
  // `reconcile`, so a status that beat its message (stream, send answer,
  // refetch) is applied the moment the message is there.
  const [statuses] = useState(() => createWamidStatusBuffer());
  const reconcile = useCallback(
    (next: ContactThread): ContactThread => applyWamidStatuses(next, statuses.statuses, serverNow()),
    [statuses],
  );

  // The thread also lives in a ref that every change writes synchronously
  // (as `useConversationList` does with its items): two events handled in one
  // tick — a message and the status of its wamid — must decide against the
  // thread as the first one left it, not the one of the last commit.
  const replaceThread = useCallback((next: ContactThread | null) => {
    threadRef.current = next;
    setThread(next);
  }, []);
  const changeThread = useCallback(
    (change: (prev: ContactThread) => ContactThread) => {
      const prev = threadRef.current;
      if (!prev) return;
      const next = reconcile(change(prev));
      if (next !== prev) replaceThread(next);
    },
    [reconcile, replaceThread],
  );

  const load = useCallback(
    async (silent: boolean) => {
      const seq = silent ? loadSeqRef.current : ++loadSeqRef.current;
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      const fetchThread = () => conversationService.getContactThread(contactKey);
      try {
        const res = silent ? await fetchUnlessOvertaken(mutations, fetchThread) : await fetchThread();
        if (res === null || seq !== loadSeqRef.current) return;
        if (silent) {
          const prev = threadRef.current;
          replaceThread(reconcile(prev ? mergeThreadRefresh(prev, res) : res));
          setError(null);
        } else {
          const buffered = pendingRef.current;
          pendingRef.current = [];
          loadedRef.current = true;
          replaceThread(reconcile(buffered.reduce((thread, apply) => apply(thread), res)));
        }
      } catch {
        // A failed background refresh keeps what is on screen.
        if (silent || seq !== loadSeqRef.current) return;
        pendingRef.current = [];
        setError('Chat konnte nicht geladen werden.');
        replaceThread(null);
      } finally {
        if (!silent && seq === loadSeqRef.current) setLoading(false);
      }
    },
    [contactKey, mutations, reconcile, replaceThread],
  );

  useEffect(() => {
    replaceThread(null);
    loadedRef.current = false;
    pendingRef.current = [];
    setAgentTyping(false);
    setStaffTyping(null);
    olderPromiseRef.current = null;
    // Statuses belong to this contact's messages only.
    statuses.clear();
    if (resyncTimerRef.current) clearTimeout(resyncTimerRef.current);
    resyncTimerRef.current = null;
    load(false);
  }, [load, statuses, replaceThread]);

  useEffect(
    () => () => {
      if (agentTimerRef.current) clearTimeout(agentTimerRef.current);
      if (staffTimerRef.current) clearTimeout(staffTimerRef.current);
      if (resyncTimerRef.current) clearTimeout(resyncTimerRef.current);
    },
    [],
  );

  const update = useCallback<ThreadUpdater>(
    (fn) => {
      mutations.tick();
      changeThread(fn);
    },
    [mutations, changeThread],
  );

  const refresh = useCallback(() => {
    void load(true);
  }, [load]);

  const loadOlder = useCallback((): Promise<boolean> => {
    const current = threadRef.current;
    if (!current?.has_more || current.contact_key !== contactKey) return Promise.resolve(false);
    if (olderPromiseRef.current) return olderPromiseRef.current;
    const before = current.sessions[0]?.conversation_id ?? null;
    setLoadingOlder(true);
    const promise = conversationService
      .getContactThread(contactKey, { before })
      .then((older) => {
        const base = threadRef.current;
        if (!base || base.contact_key !== older.contact_key) return false;
        const known = new Set(base.sessions.map((s) => s.conversation_id));
        const progressed = older.sessions.some((s) => !known.has(s.conversation_id));
        changeThread((prev) =>
          prev.contact_key === older.contact_key ? prependOlderSessions(prev, older) : prev,
        );
        return progressed;
      })
      .catch(() => {
        toast.error('Ältere Nachrichten konnten nicht geladen werden.');
        return false;
      })
      .finally(() => {
        olderPromiseRef.current = null;
        setLoadingOlder(false);
      });
    olderPromiseRef.current = promise;
    return promise;
  }, [contactKey, changeThread]);

  const ensureSession = useCallback(
    async (conversationId: number) => {
      for (let page = 0; page <= MAX_JUMP_PAGES; page += 1) {
        const current = threadRef.current;
        if (!current) return false;
        if (current.sessions.some((s) => s.conversation_id === conversationId)) return true;
        if (!current.has_more) return false;
        // eslint-disable-next-line no-await-in-loop -- pages depend on each other (before=)
        const progressed = await loadOlder();
        // A failed page (already toasted) or one without new sessions: stop,
        // instead of asking the same `before=` again.
        if (!progressed) return false;
      }
      return false;
    },
    [loadOlder],
  );

  // --- live -----------------------------------------------------------------------
  const apply = (fn: (thread: ContactThread) => ContactThread) => {
    mutations.tick();
    if (!loadedRef.current) {
      pendingRef.current.push(fn);
      return;
    }
    changeThread(fn);
  };

  useLiveEvent<WhatsAppMessageEvent>('whatsapp.message', (event) => {
    if (!event?.message || event.contact_key !== contactKey) return;
    apply((thread) => applyMessageEventToThread(thread, event));
    if (event.message.role === 'assistant') setAgentTyping(false);
    if (event.message.role === 'staff' && !sameAccount(event.message.author, me)) setStaffTyping(null);
  });

  useLiveEvent<WhatsAppTakeoverEvent>('whatsapp.takeover', (event) => {
    if (!event || event.contact_key !== contactKey) return;
    apply((thread) => applyTakeoverEventToThread(thread, event));
  });

  // Delivery status of one WhatsApp message. No mutation tick: the status is
  // remembered per wamid and applied to every state built afterwards, so an
  // older refetch cannot roll it back — and a stream of ticks must not keep
  // overtaking (and so discarding) the silent refetch after a reconnect.
  useLiveEvent<WhatsAppMessageStatusEvent>('whatsapp.message_status', (event) => {
    if (!event || event.contact_key !== contactKey) return;
    if (!statuses.record(event)) return;
    changeThread((prev) => prev);
    // The parts of a multi-part reply we have not heard of only count as "at
    // least what the thread shows": the server knows better — ask once.
    const current = threadRef.current;
    if (current && !resyncTimerRef.current && needsSiblingResync(current, event.wamid, statuses.statuses)) {
      resyncTimerRef.current = setTimeout(() => {
        resyncTimerRef.current = null;
        void load(true);
      }, STATUS_RESYNC_DELAY_MS);
    }
  });

  // The contact's WhatsApp number was blocked / released at Meta.
  useLiveEvent<WhatsAppChannelHealthEvent>('whatsapp.channel_health', (event) => {
    if (!event) return;
    // Another number's news is none of this chat's business: it must not even
    // tick the mutation clock (which would discard a refetch in flight).
    const current = threadRef.current;
    if (current && !sameAgentNumber(event.agent_number, current.agent_number)) return;
    apply((thread) => applyChannelHealthEventToThread(thread, event));
  });

  useLiveEvent<AgentTypingEvent>('whatsapp.agent_typing', (event) => {
    if (!event || event.contact_key !== contactKey) return;
    if (agentTimerRef.current) clearTimeout(agentTimerRef.current);
    setAgentTyping(!!event.active);
    if (event.active) {
      agentTimerRef.current = setTimeout(() => setAgentTyping(false), AGENT_TYPING_TIMEOUT_MS);
    }
  });

  useLiveEvent<StaffTypingEvent>('whatsapp.staff_typing', (event) => {
    if (!event || event.contact_key !== contactKey || sameAccount(event.by, me)) return;
    if (staffTimerRef.current) clearTimeout(staffTimerRef.current);
    setStaffTyping(event.active ? event.by?.trim() || 'Ein Mitarbeiter' : null);
    if (event.active) {
      staffTimerRef.current = setTimeout(() => setStaffTyping(null), STAFF_TYPING_TIMEOUT_MS);
    }
  });

  // (Re)connected → one silent refetch covers what the stream could not
  // replay; stream down → poll every 5 s.
  const refreshSilently = () => {
    load(true);
  };
  useLiveEvent('ready', refreshSilently);
  useDegradedPolling(refreshSilently);

  return { thread, loading, error, loadingOlder, agentTyping, staffTyping, update, refresh, loadOlder, ensureSession };
}

export default useContactThread;
